<template>
  <div class="audio-player-wrapper">
    <div class="audio-player-header">
      <v-icon class="audio-icon" size="18">mdi-waveform</v-icon>
      <span class="audio-filename">{{ filename }}</span>
    </div>
    <div v-if="loadError" class="audio-error">
      <v-icon size="16" color="warning">mdi-alert-circle-outline</v-icon>
      <span>Archivo de audio no disponible</span>
    </div>
    <div class="audio-controls" v-else>
      <v-btn icon class="play-btn" @click="togglePlay" :loading="isLoading">
        <v-icon size="26">{{ isPlaying ? 'mdi-pause-circle' : 'mdi-play-circle' }}</v-icon>
      </v-btn>
      <div class="progress-section">
        <div class="progress-bar-container" @click="seekAudio">
          <div class="progress-bar-bg">
            <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
            <div class="progress-thumb" :style="{ left: progressPercent + '%' }"></div>
          </div>
        </div>
        <div class="time-display">
          <span>{{ currentTimeFormatted }}</span>
          <span>{{ durationFormatted }}</span>
        </div>
      </div>
      <v-btn icon class="volume-btn" @click="toggleMute" small>
        <v-icon size="16">{{ isMuted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon>
      </v-btn>
    </div>
    <audio
      ref="audioPlayer"
      :src="src"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @ended="onAudioEnded"
      @waiting="isLoading = true"
      @canplay="isLoading = false"
      @error="onAudioError"
      style="display: none"
    ></audio>
  </div>
</template>

<script>
export default {
  props: {
    src: { type: String, required: true },
    filename: { type: String, default: 'Grabación de voz' },
  },

  data() {
    return {
      isPlaying: false,
      isMuted: false,
      isLoading: false,
      loadError: false,
      currentTime: 0,
      duration: 0,
    }
  },

  computed: {
    progressPercent() {
      return this.duration ? (this.currentTime / this.duration) * 100 : 0
    },
    currentTimeFormatted() {
      return this.formatTime(this.currentTime)
    },
    durationFormatted() {
      return this.formatTime(this.duration)
    },
  },

  beforeDestroy() {
    const audio = this.$refs.audioPlayer
    if (audio) audio.pause()
  },

  methods: {
    onAudioError() {
      this.loadError = true
    },

    togglePlay() {
      const audio = this.$refs.audioPlayer
      if (!audio || !this.src) return
      if (this.isPlaying) {
        audio.pause()
        this.isPlaying = false
      } else {
        audio.play()
        this.isPlaying = true
      }
    },

    toggleMute() {
      const audio = this.$refs.audioPlayer
      if (!audio) return
      audio.muted = !audio.muted
      this.isMuted = audio.muted
    },

    seekAudio(event) {
      const audio = this.$refs.audioPlayer
      if (!audio || !this.duration) return
      const rect = event.currentTarget.getBoundingClientRect()
      audio.currentTime = ((event.clientX - rect.left) / rect.width) * this.duration
    },

    onTimeUpdate() {
      const audio = this.$refs.audioPlayer
      if (audio) this.currentTime = audio.currentTime
    },

    onLoadedMetadata() {
      const audio = this.$refs.audioPlayer
      if (audio) this.duration = audio.duration
    },

    onAudioEnded() {
      this.isPlaying = false
      this.currentTime = 0
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    },
  },
}
</script>

<style scoped>
.audio-player-wrapper {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.25);
  border-radius: 12px;
  padding: 12px 14px;
  transition: border-color 0.3s ease;
}

.audio-player-wrapper:hover {
  border-color: rgba(var(--v-secondary-base-rgb), 0.45);
}

.audio-player-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.audio-icon {
  color: var(--v-secondary-base) !important;
  flex-shrink: 0;
}

.audio-filename {
  font-size: 0.72rem;
  color: rgba(224, 224, 224, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.audio-error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: rgba(224, 224, 224, 0.5);
  padding: 4px 0;
}

.audio-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.play-btn {
  color: var(--v-secondary-base) !important;
  flex-shrink: 0;
}

.volume-btn {
  color: rgba(224, 224, 224, 0.55) !important;
  flex-shrink: 0;
}

.progress-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-bar-container {
  cursor: pointer;
  padding: 5px 0;
}

.progress-bar-bg {
  position: relative;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.progress-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, var(--v-secondary-base), var(--v-secondary-darken1));
  border-radius: 4px;
  transition: width 0.1s linear;
}

.progress-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 9px;
  height: 9px;
  background: var(--v-secondary-base);
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(var(--v-secondary-base-rgb), 0.5);
  transition: left 0.1s linear;
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  color: rgba(224, 224, 224, 0.45);
  padding: 0 1px;
}
</style>
